'use client';

import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot='sheet' {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot='sheet-close' {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot='sheet-portal' {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot='sheet-overlay'
      className={cn(
        'ci:data-[state=open]:animate-in ci:data-[state=closed]:animate-out ci:data-[state=closed]:fade-out-0 ci:data-[state=open]:fade-in-0 ci:fixed ci:inset-0 ci:z-50 ci:bg-black/50',
        className
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = 'right',
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot='sheet-content'
        className={cn(
          'ci:bg-background ci:data-[state=open]:animate-in ci:data-[state=closed]:animate-out ci:fixed ci:z-50 ci:flex ci:flex-col ci:gap-4 ci:shadow-lg ci:transition ci:ease-in-out ci:data-[state=closed]:duration-300 ci:data-[state=open]:duration-500',
          side === 'right' &&
            'ci:data-[state=closed]:slide-out-to-right ci:data-[state=open]:slide-in-from-right ci:inset-y-0 ci:right-0 ci:h-full ci:w-3/4 ci:border-l ci:sm:max-w-sm',
          side === 'left' &&
            'ci:data-[state=closed]:slide-out-to-left ci:data-[state=open]:slide-in-from-left ci:inset-y-0 ci:left-0 ci:h-full ci:w-3/4 ci:border-r ci:sm:max-w-sm',
          side === 'top' &&
            'ci:data-[state=closed]:slide-out-to-top ci:data-[state=open]:slide-in-from-top ci:inset-x-0 ci:top-0 ci:h-auto ci:border-b',
          side === 'bottom' &&
            'ci:data-[state=closed]:slide-out-to-bottom ci:data-[state=open]:slide-in-from-bottom ci:inset-x-0 ci:bottom-0 ci:h-auto ci:border-t',
          className
        )}
        {...props}>
        {children}
        <SheetPrimitive.Close className='ci:ring-offset-background ci:focus:ring-ring ci:data-[state=open]:bg-secondary ci:absolute ci:top-4 ci:right-4 ci:rounded-xs ci:opacity-70 ci:transition-opacity ci:hover:opacity-100 ci:focus:ring-2 ci:focus:ring-offset-2 ci:focus:outline-hidden ci:disabled:pointer-events-none'>
          <XIcon className='ci:size-4' />
          <span className='ci:sr-only'>Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-header'
      className={cn('ci:flex ci:flex-col ci:gap-1.5 ci:p-4', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-footer'
      className={cn('ci:mt-auto ci:flex ci:flex-col ci:gap-2 ci:p-4', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot='sheet-title'
      className={cn('ci:text-foreground ci:font-semibold', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot='sheet-description'
      className={cn('ci:text-muted-foreground ci:text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
